import { cssAnimateNewHeight } from '../utils/animation';

/**
 * Attention
 * Be aware that this module is dependent on support for Element.prototype.closest
 * This is included as default in https://polyfill.io/v3/polyfill.min.js
 */

 /**
 * Get proper browser prefix for events such as 'ontransitionend' or 'onaminationend'
 * @param  {Node}      element   Element to attach the event
 * @param  {String}    type      The event you want a prefix for
 * @param  {Function}  callback  The callback with the correct prefix
 */
const setPrefixedEvent = (element, type, callback) => {
    const prefixes = ['webkit', 'moz', 'MS', 'o', ''];

    prefixes.forEach((prefix) => {
        if (!prefix) {
            type = type.toLowerCase();
        }
        element.addEventListener(prefix + type, callback, false);
    });
};

// Used keycodes
const keycode = {
    space: 32,
    end: 35,
    home: 36,
    arrowUp: 38,
    arrowDown: 40
};

/**
 * Function that opens new accordion and if in a group closes the active accordion
 *
 * @param {object|HTMLElement} e - click event or tab element
 */
const accordionChange = (element) => {
    if (element) {
        const accordions = element.closest('.accordions');
        const accordionTarget = document.getElementById(element.getAttribute('aria-controls'));

        if (accordions) {
            accordions.querySelectorAll('.accordion').forEach((accordion) => {
                if (accordion === accordionTarget.closest('.accordion') && accordion.classList.contains('accordion--inactive')) {
                    // Button
                    accordion.querySelector('.accordion__item').setAttribute('aria-expanded', true);
                    accordion.querySelector('.accordion__item').focus();

                    // Content
                    accordion.querySelector('.accordion__content').setAttribute('aria-hidden', false);
                    accordion.querySelector('.accordion__content').style.height = accordion.querySelector('.accordion__content').scrollHeight + 'px';
                    accordion.querySelector('.accordion__content').style.overflow = 'hidden';

                    accordion.classList.remove('accordion--inactive');
                } else {
                    // Button
                    accordion.querySelector('.accordion__item').setAttribute('aria-expanded', false);

                    // Content
                    if (!accordion.classList.contains('accordion--inactive')) {
                        accordion.querySelector('.accordion__content').setAttribute('aria-hidden', true);
                        accordion.querySelector('.accordion__content').style.height = accordion.querySelector('.accordion__content').scrollHeight + 'px';
                        accordion.querySelector('.accordion__content').style.overflow = 'hidden';

                        setTimeout(() => {
                            accordion.querySelector('.accordion__content').style.height = '0';
                        }, 20);
                    }

                    accordion.classList.add('accordion--inactive');
                }
            });
        } else {
            const accordion = element.closest('.accordion');

            // Button
            element.setAttribute('aria-expanded', !(element.getAttribute('aria-expanded') === 'true'));
            element.focus();

            // Content
            accordionTarget.setAttribute('aria-hidden', !(accordionTarget.getAttribute('aria-hidden') === 'true'));
            accordionTarget.style.height = accordionTarget.scrollHeight + 'px';
            accordionTarget.style.overflow = 'hidden';

            if (!accordion.classList.contains('accordion--inactive')) {
                setTimeout(() => {
                    accordionTarget.style.height = '0';
                }, 20);
            }

            accordion.classList.toggle('accordion--inactive');
        }
    }
};

const accordionToggle = (event) => {
    if (!(event.ctrlKey || event.metaKey || event.shiftKey)) {
        event.preventDefault();
    }

    const element = event.target;

    accordionChange(element);
};

const accordionTtransition = (event) => {
    const element = event.target;

    if (element.getAttribute('style')) {
        element.removeAttribute('style');
    }
};


/**
 * Initialise Accordions with this function
 * Will only run if given selector elements are found in DOM
 *
 * @param {string} selector - element selector string
 */
export function setupAccordions(selector = '[data-action="accordion-toggle"]') {
    const accordionButtonToggle = document.querySelectorAll('[data-action="accordion-toggle"]');

    accordionButtonToggle.forEach((button) => {
        button.addEventListener('click', accordionToggle);
        setPrefixedEvent(document.getElementById(button.getAttribute('aria-controls')), 'TransitionEnd', accordionTtransition);
    });
};

if (window.location.hash.startsWith('accordion', 1) && document.getElementById(window.location.hash.replace('#accordion-', ''))) {
    accordionChange(document.getElementById(document.getElementById(window.location.hash.replace('#accordion-', '')).getAttribute('aria-labelledby')));
}

window.addEventListener('hashchange', () => {
    if (window.location.hash.startsWith('accordion', 1) && document.getElementById(window.location.hash.replace('#', ''))) {
        accordionChange(document.getElementById(document.getElementById(window.location.hash.replace('#', '')).getAttribute('aria-labelledby')));
    }
}, false);

window.addEventListener('keydown', (event) => {
    const focusedElement = document.activeElement;
    const accordions = document.activeElement.closest('.accordions');

    if (focusedElement.getAttribute('data-action') === 'accordion-toggle') {
        if (event.keyCode === keycode.space) {
            // Toggle accordion
            event.preventDefault();
            accordionChange(focusedElement);
        }
    }
    if (focusedElement && accordions) {
        if (event.keyCode === keycode.arrowUp) {
            // Focus prevoius accordion
            if (focusedElement.closest('.accordion').previousElementSibling && focusedElement.closest('.accordion').previousElementSibling.querySelector('.accordion__item')) {
                event.preventDefault();
                focusedElement.closest('.accordion').previousElementSibling.querySelector('.accordion__item').focus();
            }
        }
        if (event.keyCode === keycode.arrowDown) {
            // Focus next accordion
            if (focusedElement.closest('.accordion').nextElementSibling && focusedElement.closest('.accordion').nextElementSibling.querySelector('.accordion__item')) {
                event.preventDefault();
                focusedElement.closest('.accordion').nextElementSibling.querySelector('.accordion__item').focus();
            }
        }
        if (event.keyCode === keycode.home) {
            // Focus first accordion
            if (accordions.querySelector('.accordion:first-child .accordion__item')) {
                event.preventDefault();
                accordions.querySelector('.accordion:first-child .accordion__item').focus();
            }
        }
        if (event.keyCode === keycode.end) {
            // Focus last accordion
            if (accordions.querySelector('.accordion:last-child .accordion__item')) {
                event.preventDefault();
                accordions.querySelector('.accordion:last-child .accordion__item').focus();
            }
        }
    }
});